import {
  MediaRecorderMimeType,
  type MediaRecorderAudioChunk,
  type MediaRecorderCapabilities,
  type MediaRecorderEngine,
  type MediaRecorderEventHandlers,
} from "@/store/audio/recording/media-types";

const DEFAULT_CHUNK_INTERVAL_MS = 1000;
const DEFAULT_MIME_TYPE = MediaRecorderMimeType.WebmOpus;
const SUPPORTED_MIME_TYPES: MediaRecorderMimeType[] = Object.values(MediaRecorderMimeType);

export type WebMediaRecorderOptions = {
  stream: MediaStream;
  chunkIntervalMs?: number;
  mimeType?: MediaRecorderMimeType;
};

export class WebMediaRecorder implements MediaRecorderEngine {
  private readonly stream: MediaStream;
  private readonly chunkIntervalMs: number;
  private readonly requestedMimeType?: MediaRecorderMimeType;

  private mediaRecorder: MediaRecorder | null = null;
  private lastTimecode: number | null = null;

  private handlers: MediaRecorderEventHandlers = {};

  private handleDataAvailableBound: ((event: BlobEvent) => void) | null = null;
  private handleErrorBound: ((event: Event) => void) | null = null;
  private handleStopBound: (() => void) | null = null;

  private readonly capabilitiesInternal: MediaRecorderCapabilities = {
    audio: true,
    transcripts: false,
    waveform: false,
    pauseResume: true,
  };

  constructor({
    stream,
    chunkIntervalMs = DEFAULT_CHUNK_INTERVAL_MS,
    mimeType,
  }: WebMediaRecorderOptions) {
    this.stream = stream;
    this.chunkIntervalMs = chunkIntervalMs;
    this.requestedMimeType = mimeType;
  }

  get capabilities(): MediaRecorderCapabilities {
    return this.capabilitiesInternal;
  }

  setEventHandlers(handlers: MediaRecorderEventHandlers): void {
    this.handlers = handlers ?? {};
  }

  async start(): Promise<void> {
    if (typeof window === "undefined" || typeof window.MediaRecorder === "undefined") {
      throw new Error("MediaRecorder API is not available in this environment.");
    }

    const recorder = new MediaRecorder(this.stream, { mimeType: this.resolveMimeType() });

    this.handleDataAvailableBound = this.handleDataAvailable.bind(this);
    this.handleErrorBound = this.handleError.bind(this);
    this.handleStopBound = this.handleStop.bind(this);

    recorder.addEventListener("dataavailable", this.handleDataAvailableBound);
    recorder.addEventListener("error", this.handleErrorBound);
    recorder.addEventListener("stop", this.handleStopBound);

    this.mediaRecorder = recorder;
    this.lastTimecode = null;

    recorder.start(this.chunkIntervalMs);
    this.handlers.onStart?.();
  }

  async stop(): Promise<void> {
    if (!this.mediaRecorder) return;
    this.mediaRecorder.stop();
  }

  async pause(): Promise<void> {
    if (!this.mediaRecorder) return;
    this.mediaRecorder.pause();
  }

  async resume(): Promise<void> {
    if (!this.mediaRecorder) return;
    this.mediaRecorder.resume();
  }

  dispose(): void {
    void this.stop();
    this.cleanupRecorder();
    this.stream.getTracks().forEach(track => track.stop());
  }

  private handleDataAvailable(event: BlobEvent) {
    if (!event.data || event.data.size === 0) return;

    const previousTimecode = this.lastTimecode;
    const durationMs =
      typeof event.timecode === "number" && previousTimecode !== null
        ? Math.max(event.timecode - previousTimecode, this.chunkIntervalMs)
        : this.chunkIntervalMs;

    this.lastTimecode =
      typeof event.timecode === "number"
        ? event.timecode
        : previousTimecode !== null
          ? previousTimecode + durationMs
          : durationMs;

    const chunk: MediaRecorderAudioChunk = {
      blob: event.data,
      durationMs,
      mimeType: resolveMimeType({
        reportedType: event.data.type,
        fallback: this.resolveMimeType(),
      }),
      byteLength: event.data.size,
    };

    try {
      this.handlers.onAudioChunk?.({ chunk });
    } catch (error) {
      this.handlers.onError?.({ error });
    }
  }

  private handleError(event: Event) {
    const error = (event as { error?: DOMException }).error ?? event;
    this.handlers.onError?.({ error });
  }

  private handleStop() {
    this.handlers.onStop?.();
    this.cleanupRecorder();
  }

  private cleanupRecorder() {
    if (!this.mediaRecorder) return;

    if (this.handleDataAvailableBound) {
      this.mediaRecorder.removeEventListener("dataavailable", this.handleDataAvailableBound);
    }
    if (this.handleErrorBound) {
      this.mediaRecorder.removeEventListener("error", this.handleErrorBound);
    }
    if (this.handleStopBound) {
      this.mediaRecorder.removeEventListener("stop", this.handleStopBound);
    }

    this.mediaRecorder = null;
    this.handleDataAvailableBound = null;
    this.handleErrorBound = null;
    this.handleStopBound = null;
    this.lastTimecode = null;
  }

  private resolveMimeType(): MediaRecorderMimeType {
    if (this.requestedMimeType) {
      return this.requestedMimeType;
    }

    if (typeof MediaRecorder.isTypeSupported === "function") {
      for (const candidate of SUPPORTED_MIME_TYPES) {
        if (MediaRecorder.isTypeSupported(candidate)) {
          return candidate;
        }
      }
    }

    return DEFAULT_MIME_TYPE;
  }
}

function resolveMimeType({
  reportedType,
  fallback,
}: {
  reportedType: string;
  fallback: MediaRecorderMimeType;
}): MediaRecorderMimeType {
  if ((SUPPORTED_MIME_TYPES as string[]).includes(reportedType)) {
    return reportedType as MediaRecorderMimeType;
  }
  return fallback;
}
